import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCard = ({ notification, onMarkAsRead, onDelete }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const getNotificationIcon = (type) => {
    const icons = {
      deadline: 'Clock',
      result: 'CheckCircle',
      leaderboard: 'TrendingUp',
      tournament: 'Trophy'
    };
    return icons?.[type] || 'Bell';
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'urgent') {
      return {
        bg: 'var(--color-error)',
        bgLight: 'rgba(239, 68, 68, 0.1)',
        border: 'var(--color-error)'
      };
    }

    const colors = {
      deadline: {
        bg: 'var(--color-warning)',
        bgLight: 'rgba(251, 191, 36, 0.1)',
        border: 'var(--color-warning)'
      },
      result: {
        bg: 'var(--color-success)',
        bgLight: 'rgba(34, 197, 94, 0.1)',
        border: 'var(--color-success)'
      },
      leaderboard: {
        bg: 'var(--color-accent)',
        bgLight: 'rgba(59, 130, 246, 0.1)',
        border: 'var(--color-accent)'
      },
      tournament: {
        bg: 'var(--color-primary)',
        bgLight: 'rgba(124, 58, 237, 0.1)',
        border: 'var(--color-primary)'
      }
    };
    return colors?.[type] || colors?.tournament;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return notificationTime?.toLocaleDateString();
    }
  };

  const handleNavigate = () => {
    if (!notification?.isRead) {
      onMarkAsRead(notification?.id);
    }

    // Navigate to relevant screen based on notification type
    if (notification?.matchId) {
      navigate(`/match-predictions/${notification?.matchId}`);
    } else if (notification?.tournamentId) {
      navigate(`/tournament-details/${notification?.tournamentId}`);
    }
  };

  const handleMarkAsRead = (e) => {
    e?.stopPropagation();
    onMarkAsRead(notification?.id);
  };

  const handleDelete = (e) => {
    e?.stopPropagation();
    onDelete(notification?.id);
  };

  const colors = getNotificationColor(notification?.type, notification?.priority);
  const isPriorityHigh = notification?.priority === 'urgent' || notification?.priority === 'high';

  return (
    <div
      className={`bg-card rounded-lg shadow-elevation-sm hover:shadow-elevation-md transition-all duration-200 overflow-hidden ${
        !notification?.isRead ? 'border-l-4' : 'border-l-2 opacity-75'
      }`}
      style={{
        borderLeftColor: colors?.border
      }}
    >
      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3 md:gap-4">
          {/* Icon */}
          <div
            className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors?.bgLight }}
          >
            <Icon
              name={getNotificationIcon(notification?.type)}
              size={20}
              color={colors?.bg}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-1">
                  {notification?.title}
                  {!notification?.isRead && (
                    <span className="inline-block w-2 h-2 bg-primary rounded-full ml-2"></span>
                  )}
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {notification?.description}
                </p>
              </div>

              {/* Priority Badge */}
              {isPriorityHigh && (
                <span
                  className="flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: colors?.bgLight,
                    color: colors?.bg
                  }}
                >
                  {notification?.priority === 'urgent' ? 'Urgent' : 'High'}
                </span>
              )}
            </div>

            {/* Additional Info */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground mb-3">
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                <span>{formatTimeAgo(notification?.timestamp)}</span>
              </div>

              {notification?.tournamentName && (
                <div className="flex items-center gap-1">
                  <Icon name="Trophy" size={14} />
                  <span>{notification?.tournamentName}</span>
                </div>
              )}

              {notification?.points && (
                <div
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    color: 'var(--color-success)'
                  }}
                >
                  <Icon name="Award" size={14} />
                  <span>+{notification?.points} pts</span>
                </div>
              )}

              {notification?.rankChange && (
                <div
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: colors?.bgLight,
                    color: colors?.bg
                  }}
                >
                  <Icon name="TrendingUp" size={14} />
                  <span>+{notification?.rankChange} positions</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              {(notification?.matchId || notification?.tournamentId) && (
                <Button
                  size="sm"
                  variant="outline"
                  iconName="ExternalLink"
                  onClick={handleNavigate}
                  className="text-xs md:text-sm"
                >
                  View Details
                </Button>
              )}

              {!notification?.isRead && (
                <Button
                  size="sm"
                  variant="ghost"
                  iconName="Check"
                  onClick={handleMarkAsRead}
                  className="text-xs md:text-sm"
                >
                  Mark as Read
                </Button>
              )}

              <Button
                size="sm"
                variant="ghost"
                iconName="Trash2"
                onClick={handleDelete}
                className="text-xs md:text-sm text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;